import { Link, type LinkProps } from "@tanstack/react-location"
import { Icon, type IconType } from "materia"
import type { ComponentPropsWithoutRef, ElementType } from "react"
import { modifiers } from "ui-tools"
import s from "./IconButton.module.scss"

type IconButtonProps<C extends ElementType> = {
  /**
   * Name of the icon to display.
   */
  icon: IconType
  /**
   * Determines the size of the button - <Icon> inside always remains "medium".
   */
  size?: "small" | "medium" | "large" | "extraLarge"
  /**
   * Variant of the button.
   */
  variant?: "default" | "ghost"
} & (({ as: C } & ComponentPropsWithoutRef<C>) | ({ as: "Link" } & LinkProps))
export const IconButton = <C extends ElementType = "button">({
  as,
  className = "", // IMO className should be forbidden as a prop. It is currently used to absolutely position the button when it's used as a Close button. Maybe it should be wrapped in an absolutely positioned container instead?
  icon,
  size = "large",
  variant = "default",
  ...props
}: IconButtonProps<C>) => {
  const modifiedClassName = `${modifiers(
    s,
    "iconButton",
    `size-${size}`,
    `variant-${variant}`
  )} ${className}`

  if (as === "Link") {
    return (
      <Link {...props} className={modifiedClassName}>
        <Icon icon={icon} size="medium" />
      </Link>
    )
  }

  const Component = as as ElementType

  return (
    <Component {...props} className={modifiedClassName}>
      <Icon icon={icon} size="medium" />
    </Component>
  )
}
